import React, { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { Drawer, Form, Input, Skeleton } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import CustomButton from "../../../commonComponents/button";
import { GET_CO_BUYER_DETAIL } from "../../../../graphql/query";
import { displayPhone, displayEmail } from "../engagementHelpers";
import { DEFAULT_COUNTRY } from "../../../../config";
import { useDevice } from "../../../../hooks/useDevice";

const Banner = ({ title, extra }) => (
  <div className="flex items-center justify-between text-black-88 text-sm font-semibold bg-[#F5F5F5] px-3 py-2 rounded">
    <span>{title}</span>
    {extra}
  </div>
);

const Field = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <span className="text-black-45 text-xs">{label}</span>
    <span className="text-black-88 text-sm">{value || "--"}</span>
  </div>
);

const CoBuyerDetailDrawer = ({ open, onClose, coBuyer, isEdit = false, onSave }) => {
  const { isMobile } = useDevice();
  const leadId = coBuyer?.leadId || coBuyer?.lead?.id;
  const isCreateMode = isEdit && !leadId;
  const [form] = Form.useForm();

  const { data, loading } = useQuery(GET_CO_BUYER_DETAIL, {
    variables: { leadIds: [+leadId] },
    skip: !leadId || !open || isCreateMode,
    fetchPolicy: "network-only",
  });

  const lead = data?.getLeadsById?.[0] || {};
  const { firstName, lastName, addresses } = lead;

  const primaryPhone = displayPhone(lead);
  const primaryEmail = displayEmail(lead);
  const address = addresses?.[0] || {};

  // Ensure phone has country code for PhoneInput (expects E.164 format like +12125551234)
  const getRawPhone = () => {
    const phones = lead?.phoneNumbers || [];
    const primary = phones.find(p => p.isPrimary) || phones[0];
    if (!primary?.phone) return "";
    const phone = primary.phone.replace(/[\s()-]/g, "");
    return phone.startsWith("+") ? phone : `+1${phone}`;
  };

  useEffect(() => {
    if (isEdit && !isCreateMode && lead?.id) {
      form.setFieldsValue({
        firstName,
        lastName,
        phone: getRawPhone(),
        email: primaryEmail !== "N/A" ? primaryEmail : "",
        addressLine1: address.addressLine1 || "",
        addressLine2: address.addressLine2 || "",
        city: address.city || "",
        state: address.state || "",
        postalCode: address.postalCode || "",
        country: address.country || "",
      });
    }
  }, [lead?.id]);

  const handleSubmit = values => {
    onSave?.(values);
  };

  const drawerTitle = isCreateMode
    ? "Add new lead (Co-buyer)"
    : `Co-Buyer - ${firstName || coBuyer?.lead?.firstName || ""} ${
        lastName || coBuyer?.lead?.lastName || ""
      }`;

  const renderSkeleton = () => (
    <div className="flex flex-col gap-4">
      <Banner title="Personal details" />
      <div className="grid grid-cols-2 gap-4 mb-10">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-1">
            <Skeleton.Input active size="small" style={{ width: 80, height: 14 }} />
            <Skeleton.Input active size="small" block />
          </div>
        ))}
      </div>
      <Banner title="Address (Optional)" />
      <div className="grid grid-cols-2 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-1">
            <Skeleton.Input active size="small" style={{ width: 80, height: 14 }} />
            <Skeleton.Input active size="small" block />
          </div>
        ))}
      </div>
    </div>
  );

  const renderReadOnly = () => (
    <div className="flex flex-col gap-4">
      <Banner title="Personal details" />
      <div className="grid grid-cols-2 gap-4 mb-10">
        <Field label="First name" value={firstName} />
        <Field label="Last name" value={lastName} />
        <Field label="Phone number" value={primaryPhone} />
        <Field label="Email" value={primaryEmail} />
      </div>
      <Banner title="Address (Optional)" />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Address line 1" value={address.addressLine1} />
        <Field label="Address line 2" value={address.addressLine2} />
        <Field label="City" value={address.city} />
        <Field label="State" value={address.state} />
        <Field label="Postal code" value={address.postalCode} />
        <Field label="Country" value={address.country} />
      </div>
    </div>
  );

  const renderEditForm = () => (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <Banner title="Personal details" />
      <div className="grid grid-cols-2 gap-x-4 mt-4 mb-6">
        <Form.Item
          label={<span className="form-field-label">First name</span>}
          name="firstName"
          required
          rules={[{ required: true, message: "First name is required" }]}
        >
          <Input placeholder="First name" />
        </Form.Item>
        <Form.Item
          label={<span className="form-field-label">Last name</span>}
          name="lastName"
          required
          rules={[{ required: true, message: "Last name is required" }]}
        >
          <Input placeholder="Last name" />
        </Form.Item>
        <Form.Item
          label={<span className="form-field-label">Phone number</span>}
          name="phone"
          required
          rules={[
            { required: true, message: "Phone number is required" },
            {
              validator: (_, value) =>
                !value || isValidPhoneNumber(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error("Invalid phone number")),
            },
          ]}
        >
          <PhoneInput
            international
            className="phoneInputCss"
            withCountryCallingCode
            countryCallingCodeEditable={false}
            defaultCountry={DEFAULT_COUNTRY}
            placeholder="Phone number"
          />
        </Form.Item>
        <Form.Item
          label={<span className="form-field-label">Email (optional)</span>}
          name="email"
          rules={[{ type: "email", message: "Invalid email address" }]}
        >
          <Input placeholder="Email" />
        </Form.Item>
      </div>

      <Banner
        title="Address (Optional)"
        extra={
          <CustomButton
            type="link"
            size="small"
            className="p-0 font-semibold"
            btnText="Copy from buyer"
            htmlType="button"
          />
        }
      />
      <div className="grid grid-cols-2 gap-x-4 mt-4">
        <Form.Item
          label={<span className="form-field-label">Address line 1</span>}
          name="addressLine1"
        >
          <Input placeholder="Address line 1" />
        </Form.Item>
        <Form.Item
          label={<span className="form-field-label">Address line 2</span>}
          name="addressLine2"
        >
          <Input placeholder="Address line 2" />
        </Form.Item>
        <Form.Item label={<span className="form-field-label">City</span>} name="city">
          <Input placeholder="City" />
        </Form.Item>
        <Form.Item label={<span className="form-field-label">State</span>} name="state">
          <Input placeholder="State" />
        </Form.Item>
        <Form.Item label={<span className="form-field-label">Postal code</span>} name="postalCode">
          <Input placeholder="Postal code" />
        </Form.Item>
        <Form.Item label={<span className="form-field-label">Country</span>} name="country">
          <Input placeholder="Country" />
        </Form.Item>
      </div>
    </Form>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={isMobile ? "100%" : 600}
      closable
      title={
        isEdit ? (
          <div className="flex items-center gap-2">
            <ArrowLeftOutlined className="cursor-pointer" onClick={onClose} />
            <span>{drawerTitle}</span>
          </div>
        ) : (
          drawerTitle
        )
      }
      footer={
        <div className="flex justify-start gap-2">
          {isEdit ? (
            <>
              <CustomButton type="primary" btnText="Save" handleClick={() => form.submit()} />
              <CustomButton btnText="Cancel" handleClick={onClose} />
            </>
          ) : (
            <CustomButton btnText="Close" handleClick={onClose} />
          )}
        </div>
      }
    >
      {loading && !isCreateMode ? renderSkeleton() : isEdit ? renderEditForm() : renderReadOnly()}
    </Drawer>
  );
};

export default CoBuyerDetailDrawer;
